import React, { useCallback, useState } from 'react';
import { useSnackbar } from 'notistack';
import type XLSX from 'xlsx-ugnis';
import { UploadStep } from './UploadStep/UploadStep';
import { SelectHeaderStep } from './SelectHeaderStep/SelectHeaderStep';
import { SelectSheetStep } from './SelectSheetStep/SelectSheetStep';
import { mapWorkbook } from '../utils/mapWorkbook';
import { ValidationStep } from './ValidationStep/ValidationStep';
import { addErrorsAndRunHooks } from './ValidationStep/utils/dataMutations';
import { MatchColumnsStep } from './MatchColumnsStep/MatchColumnsStep';
import { exceedsMaxRecords } from '../utils/exceedsMaxRecords';
import { useRsi } from '../hooks/useRsi';
import type { Data, RawData } from '../types';
import { Meta } from './ValidationStep/types';
import { LinearProgress } from '@beeline.com/bp-design-system';

export enum StepType {
  upload = 'upload',
  selectSheet = 'selectSheet',
  selectHeader = 'selectHeader',
  matchColumns = 'matchColumns',
  validateData = 'validateData'
}
export type StepState =
  | {
      type: StepType.upload;
    }
  | {
      type: StepType.selectSheet;
      workbook: XLSX.WorkBook;
    }
  | {
      type: StepType.selectHeader;
      data: RawData[];
    }
  | {
      type: StepType.matchColumns;
      data: RawData[];
      headerValues: RawData;
      hasChanged?: boolean;
    }
  | {
      type: StepType.validateData;
      data: (Data<string> & Meta)[];
      hasChanged?: boolean;
    };

interface Props {
  state: StepState;
  onNext: (v: StepState) => void;
  onBack?: () => void;
  setHasChanged?: (hasChanged: boolean) => void;
}

export const UploadFlow = ({ state, onNext, onBack, setHasChanged }: Props) => {
  const { maxRecords, translations, uploadStepHook, selectHeaderStepHook, matchColumnsStepHook, fields, tableHook } =
    useRsi();
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const { enqueueSnackbar } = useSnackbar();
  const errorToast = useCallback(
    (description: string) => {
      enqueueSnackbar(
        <>
          {translations.alerts.toast.error}
          <br />
          {description}
        </>,
        {
          variant: 'error'
        }
      );
    },
    [enqueueSnackbar, translations]
  );

  switch (state.type) {
    case StepType.upload:
      return (
        <UploadStep
          onContinue={async (workbook, file) => {
            setUploadedFile(file);
            const isSingleSheet = workbook.SheetNames.length === 1;
            if (isSingleSheet) {
              if (
                maxRecords &&
                exceedsMaxRecords(workbook.Sheets[workbook.SheetNames[0] as string] as XLSX.WorkSheet, maxRecords)
              ) {
                errorToast(translations.uploadStep.maxRecordsExceeded(maxRecords));
                return;
              }
              try {
                const mappedWorkbook = await uploadStepHook(mapWorkbook(workbook));
                onNext({
                  type: StepType.selectHeader,
                  data: mappedWorkbook
                });
              } catch (e) {
                errorToast((e as Error).message);
              }
            } else {
              onNext({ type: StepType.selectSheet, workbook });
            }
          }}
        />
      );
    case StepType.selectSheet:
      return (
        <SelectSheetStep
          sheetNames={state.workbook.SheetNames}
          onContinue={async (sheetName) => {
            if (maxRecords && exceedsMaxRecords(state.workbook.Sheets[sheetName] as XLSX.WorkSheet, maxRecords)) {
              errorToast(translations.uploadStep.maxRecordsExceeded(maxRecords));
              return;
            }
            try {
              const mappedWorkbook = await uploadStepHook(mapWorkbook(state.workbook, sheetName));
              onNext({
                type: StepType.selectHeader,
                data: mappedWorkbook
              });
            } catch (e) {
              errorToast((e as Error).message);
            }
          }}
          onBack={onBack}
        />
      );
    case StepType.selectHeader:
      return (
        <SelectHeaderStep
          data={state.data}
          onContinue={async (...args) => {
            try {
              const { data, headerValues } = await selectHeaderStepHook(...args);
              onNext({
                type: StepType.matchColumns,
                data,
                headerValues
              });
            } catch (e) {
              errorToast((e as Error).message);
            }
          }}
          onBack={onBack}
        />
      );
    case StepType.matchColumns:
      return (
        <MatchColumnsStep
          data={state.data}
          headerValues={state.headerValues}
          onContinue={async (values, rawData, columns) => {
            try {
              // Trimming empty values to not send this data for validation.
              const trimmedValues = trimEmptyValues(values);
              // Note if matchColumnsStepHook is not defined the default
              // implementation will just return the values that are passed in.
              const data = await matchColumnsStepHook(trimmedValues, rawData, columns);
              // Don't pass in rowHook to improve performance.
              const dataWithMeta = await addErrorsAndRunHooks(data, fields, undefined, tableHook);
              onNext({
                type: StepType.validateData,
                data: dataWithMeta
              });
            } catch (e) {
              errorToast((e as Error).message);
            }
          }}
          onBack={onBack}
          setHasChanged={setHasChanged}
        />
      );
    case StepType.validateData:
      return (
        <ValidationStep
          initialData={state.data}
          file={uploadedFile as File}
          onBack={onBack}
          setHasChanged={setHasChanged}
        />
      );
    default:
      return <LinearProgress />;
  }
};

export function trimEmptyValues<T extends Record<string, any>>(values: T[]): T[] {
  return values.map((obj) => {
    const trimmedObj: Record<string, any> = {};
    Object.keys(obj).forEach((key) => {
      const value = obj[key];
      trimmedObj[key] = typeof value === 'string' ? value.trim() : value;
    });

    return trimmedObj as T;
  });
}
